import * as React from 'react';

import type {
  UseCurriculum_AcademicEventFragment as AcademicEvent,
  UseCurriculum_AcademicTermFragment as AcademicTerm,
} from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/dpmSharedFragments';
import useCurriculum from 'bundles/degree-home/components/v3-dpm/lib/useCurriculum';
import type { RegistrationDates } from 'bundles/degree-home/components/v3-dpm/types/calendarTypes';

export interface TermStoreEntry extends RegistrationDates {
  term: AcademicTerm;
}
export type TermStore = Record<string, TermStoreEntry>;

export interface TermStoreContext {
  terms: TermStore | undefined;
}
const termStoreContext = React.createContext<TermStoreContext>({
  terms: undefined,
});

export const useTermStore = () => React.useContext(termStoreContext);

/**
 * Get a tuple of the registration dates for a given term's events.
 *
 * Designed with strict failure in mind, this only returns a tuple of dates (success)
 * or a tuple of undefined (any unexpected case)
 */
export const getRegistrationDatesForASingleTerm = (
  events: AcademicEvent[] | undefined | null
): [Date, Date] | [undefined, undefined] => {
  if (!events) {
    return [undefined, undefined];
  }
  const startDates: AcademicEvent[] = [];
  const endDates: AcademicEvent[] = [];

  events.forEach((evt) => {
    if (evt.eventType === 'REGISTRATION_START') {
      startDates.push(evt);
    } else if (evt.eventType === 'REGISTRATION_END') {
      endDates.push(evt);
    }
  });

  if (startDates.length !== 1 || endDates.length !== 1) {
    return [undefined, undefined];
  }

  // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const startDate = new Date(startDates[0].eventDate);
  // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const endDate = new Date(endDates[0].eventDate);

  return [startDate, endDate];
};

/** Indexes terms by ID for faster access  */
export const termStoreIndexer = (terms: AcademicTerm[]): TermStore => {
  const termsToReturn: TermStore = {};

  terms.forEach((term) => {
    const [registrationStartDate, registrationEndDate] = getRegistrationDatesForASingleTerm(term.events);
    termsToReturn[term.id] = {
      term,
      registrationStartDate,
      registrationEndDate,
    };
  });

  return termsToReturn;
};

/** ID-indexed term collection to reduce array iteration */
export const TermStoreProvider = ({
  __overrideForStorybook,
  children,
}: React.PropsWithChildren<{
  __overrideForStorybook?: TermStoreContext;
}>) => {
  const { data } = useCurriculum();
  const { Provider } = termStoreContext;

  const terms: TermStore | undefined = React.useMemo(() => {
    const dataTerms = data?.Degree?.queryById?.academicTerms;
    if (!dataTerms) {
      return undefined;
    }
    return termStoreIndexer(dataTerms);
  }, [data?.Degree?.queryById?.academicTerms]);

  if (__overrideForStorybook) {
    return <Provider value={__overrideForStorybook}>{children}</Provider>;
  }

  return <Provider value={{ terms }}>{children}</Provider>;
};
